import { useEffect, useState, useRef, useCallback } from 'react';
import { Box, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { StaticDateRangePicker, LocalizationProvider, CalendarPickerSkeleton, DatePicker } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { loadDashboardData, isDashboardDataLoaded, getDashboardMinDate, getDashboardMaxDate } from '../Utilities';
import DayjsAdapter from '@mui/lab/AdapterDayjs';
import CameraSelection from '../CameraSelection';
import i18next from 'i18next';
import dayjs from 'dayjs';

function rangeOverlaps(start, end, min, max) {
    return start && end && min && max && !end.isBefore(min) && !start.isAfter(max)
}

export default function DashboardControls({ onStateChange, siderWidth, loading: parentLoading, timestamp, enabledCameraIds }) {

    const { CAMERAS, BASEURL } = window.conf
    const { t } = useTranslation()

    const today = dayjs().startOf('day')
    const dashboardCameras = enabledCameraIds?.length ? CAMERAS.filter(elem => enabledCameraIds.includes(elem.id)) : CAMERAS
    const moreThanOneCam = CAMERAS.length > 1
    const daySize = 34

    // use shared timestamp from Redux (set by Archive/Events) as initial date; best range chosen after data loads
    const initialDate = timestamp ? dayjs(timestamp) : null
    const [selectedCamera, setSelectedCamera] = useState(dashboardCameras[0]?.id ?? CAMERAS?.[0]?.id)
    const [dateRange, setDateRange] = useState(initialDate ? [initialDate, initialDate] : [today, today])
    const [predefinedRange, setPredefinedRange] = useState('')
    const [forceRefresh, setForceRefresh] = useState(true)
    const [dataLoading, setDataLoading] = useState(true)
    const [dataMinDate, setDataMinDate] = useState(null)
    const [dataMaxDate, setDataMaxDate] = useState(null)

    const onStateChangeRef = useRef(onStateChange)
    onStateChangeRef.current = onStateChange

    const minDate = dataMinDate ? dayjs(dataMinDate) : today.subtract(1, 'year')
    const maxDate = dataMaxDate ? dayjs(dataMaxDate) : today
    const disableControls = parentLoading || dataLoading

    // stable drill-down handler (doesn't change on every render)
    const handleDrillDown = useCallback((dateString) => {
        const drillDate = dayjs(dateString)
        setDateRange([drillDate, drillDate])
        setPredefinedRange('')
        setForceRefresh(prev => !prev)
    }, [])

    // load dashboard data on mount, then validate/adjust initial dateRange
    useEffect(() => {
        const loadData = async () => {

            if (!isDashboardDataLoaded()) {
                setDataLoading(true)
                const camId = selectedCamera ?? CAMERAS?.[0]?.id ?? 0
                const dataUrl = `${BASEURL}cam${camId + 1}/rag_data/`
                await loadDashboardData(dataUrl)
            }

            const min = getDashboardMinDate()
            const max = getDashboardMaxDate()
            setDataMinDate(min)
            setDataMaxDate(max)

            // after data is loaded: choose best initial dateRange
            if (min && max) {
                const dMin = dayjs(min)
                const dMax = dayjs(max)
                const needsFallback = !initialDate || !rangeOverlaps(dateRange[0], dateRange[1], dMin, dMax)

                if (needsFallback) {
                    const yesterday = today.subtract(1, 'day')
                    const weekAgo = today.subtract(1, 'week')
                    const lastMonthStart = today.subtract(1, 'month').date(1)
                    const lastMonthEnd = today.subtract(1, 'month').endOf('month')

                    if (rangeOverlaps(today, today, dMin, dMax)) {
                        setDateRange([today, today])
                        setPredefinedRange('')
                    } else if (rangeOverlaps(yesterday, yesterday, dMin, dMax)) {
                        setDateRange([yesterday, yesterday])
                        setPredefinedRange('day')
                    } else if (rangeOverlaps(weekAgo, today, dMin, dMax)) {
                        setDateRange([weekAgo, today])
                        setPredefinedRange('week')
                    } else if (rangeOverlaps(lastMonthStart, lastMonthEnd, dMin, dMax)) {
                        setDateRange([lastMonthStart, lastMonthEnd])
                        setPredefinedRange('month')
                    } else {
                        setDateRange([dMin, dMax])
                        setPredefinedRange('total')
                    }
                    setForceRefresh(prev => !prev)
                }
            }

            setDataLoading(false)
        }
        loadData()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // notify parent on actual state changes (not on callback identity)
    useEffect(() => {
        if (onStateChangeRef.current) {
            onStateChangeRef.current({
                selectedCamera,
                dateRange,
                dataReady: !dataLoading && isDashboardDataLoaded(),
                onDrillDown: handleDrillDown
            })
        }
    }, [selectedCamera, dateRange, dataLoading, handleDrillDown])

    const onChangeDateRange = (newRange) => {
        if (newRange?.length) {
            setDateRange([newRange[0], newRange[1]])
            setPredefinedRange('')
        }
    }

    const onChangeStartDate = (startDate) => {
        if (startDate) {
            setDateRange([startDate, dateRange[1]])
            setPredefinedRange('')
        }
    }

    const onChangeEndDate = (endDate) => {
        if (endDate) {
            setDateRange([dateRange[0], endDate])
            setPredefinedRange('')
        }
    }

    const onChangePredefinedSpan = (_, newSpan) => {
        if (!newSpan) return

        switch (newSpan) {
            case 'day':
                const yesterday = today.subtract(1, 'day')
                setDateRange([yesterday, yesterday])
                break
            case 'week':
                setDateRange([today.subtract(1, 'week'), today])
                break
            case 'month':
                const lastMonth = today.subtract(1, 'month')
                setDateRange([lastMonth.date(1), lastMonth.endOf('month')])
                break
            case 'total':
                setDateRange([minDate, maxDate])
                break
            default:
                break
        }

        setPredefinedRange(newSpan)
        setForceRefresh(prev => !prev)
    }

    return (
        <Box>
            {/* camera selection */}
            {moreThanOneCam && (
                <CameraSelection
                    value={selectedCamera}
                    onChange={setSelectedCamera}
                    disabled={disableControls}
                    availableCameras={enabledCameraIds}
                />
            )}

            {/* start / end date pickers */}
            <LocalizationProvider
                dateAdapter={DayjsAdapter}
                locale={i18next.language}
            >
                <Box sx={{ display: 'flex' }}>
                    <DatePicker
                        label={t('date.start_date')}
                        inputFormat={t('date.format.short')}
                        mask={t('date.mask.short')}
                        renderInput={props =>
                            <TextField
                                {...props}
                                sx={{
                                    width: '100%',
                                    userSelect: 'none',
                                    '& .MuiOutlinedInput-root': {
                                        '& > fieldset': { borderColor: '#4caf50' },
                                    },
                                    '& .MuiInputAdornment-root': {
                                        margin: '-4px'
                                    }
                                }}
                            />
                        }
                        value={dateRange[0]}
                        onChange={onChangeStartDate}
                        cancelText={t('actions.cancel')}
                        toolbarTitle={t('date.select_date')}
                        minDate={minDate}
                        maxDate={dateRange[1]}
                        loading={dataLoading}
                        disabled={disableControls}
                        renderLoading={() => <CalendarPickerSkeleton />}
                    />

                    <p style={{
                        paddingLeft: 12,
                        paddingRight: 12,
                        height: '100%',
                        alignItems: 'center',
                        userSelect: 'none'
                    }}>
                        {t('date.to')}
                    </p>

                    <DatePicker
                        label={t('date.end_date')}
                        inputFormat={t('date.format.short')}
                        mask={t('date.mask.short')}
                        renderInput={props =>
                            <TextField
                                {...props}
                                sx={{
                                    width: '100%',
                                    userSelect: 'none',
                                    '& .MuiOutlinedInput-root': {
                                        '& > fieldset': { borderColor: '#ed6c02' },
                                    },
                                    '& .MuiInputAdornment-root': {
                                        margin: '-4px',
                                    }
                                }}
                            />
                        }
                        value={dateRange[1]}
                        onChange={onChangeEndDate}
                        cancelText={t('actions.cancel')}
                        toolbarTitle={t('date.select_date')}
                        minDate={dateRange[0]}
                        maxDate={maxDate}
                        loading={dataLoading}
                        disabled={disableControls}
                        renderLoading={() => <CalendarPickerSkeleton />}
                    />
                </Box>
            </LocalizationProvider>

            {/* static calendar (date range picker) */}
            <Box
                sx={{
                    '& > div': {
                        minWidth: siderWidth,
                        pt: 2
                    },
                    '& > div > div': {
                        border: '1px solid',
                        borderColor: '#BDBDBD',
                        borderRadius: '4px'
                    },
                    '& > div > div, & > div > div > div, & > div > div > div > div': {
                        width: siderWidth - 2,
                    },
                    '& .MuiTypography-caption': {
                        width: daySize + 4,
                        margin: 0
                    },
                    '& .PrivatePickersSlideTransition-root': {
                        minHeight: daySize * 7.2,
                        overflow: 'hidden'
                    },
                    '& .PrivatePickersSlideTransition-root [role="grid"]': {
                        width: siderWidth
                    },
                    '& .MuiPickersDay-root': {
                        width: daySize,
                        height: daySize
                    }
                }}
            >
                <LocalizationProvider
                    dateAdapter={DayjsAdapter}
                    locale={i18next.language}
                    key={forceRefresh}
                >
                    <StaticDateRangePicker
                        calendars={1}
                        displayStaticWrapperAs="desktop"
                        mask={t('date.mask.full')}
                        value={dateRange}
                        onChange={onChangeDateRange}
                        renderInput={() => {}}
                        minDate={minDate}
                        maxDate={maxDate}
                        loading={dataLoading}
                        disabled={disableControls}
                        renderLoading={() => <CalendarPickerSkeleton />}
                    />
                </LocalizationProvider>
            </Box>

            {/* preset buttons */}
            <ToggleButtonGroup
                color="primary"
                exclusive
                onChange={onChangePredefinedSpan}
                value={predefinedRange}
                fullWidth
                orientation="vertical"
                sx={{ pt: 2 }}
            >
                <ToggleButton
                    disabled={disableControls || !rangeOverlaps(today.subtract(1, 'day'), today.subtract(1, 'day'), minDate, maxDate)}
                    value="day"
                >
                    {t('date.yesterday')}
                </ToggleButton>
                <ToggleButton
                    disabled={disableControls || !rangeOverlaps(today.subtract(1, 'week'), today, minDate, maxDate)}
                    value="week"
                >
                    {t('date.last_week')}
                </ToggleButton>
                <ToggleButton
                    disabled={disableControls || !rangeOverlaps(today.subtract(1, 'month').date(1), today.subtract(1, 'month').endOf('month'), minDate, maxDate)}
                    value="month"
                >
                    {t('date.last_month')}
                </ToggleButton>
                <ToggleButton
                    disabled={disableControls}
                    value="total"
                >
                    {t('pages.dashboard.preset_total')}
                </ToggleButton>
            </ToggleButtonGroup>
        </Box>
    )
}
